import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { convertEpochToDateandTime } from '../../../../../utils/commonFunctions';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';

type ReturnedType = any;
type ThunkArg = {
  loadList: string;
  trailerName: string;
  truckNumber: string;
  loadType: string;
  truckLoad: string;
  isInspectionNeeded: number;
  truckLoadType: string;
};

interface Dispatch {
  loadList: string;
  trailerName: string;
  truckNumber: string;
  isInspectionNeeded: number;
}

type initialStateProps = {
  loadId: any;
  isLoading: boolean;
  type: string;
  data: any;
  error: string | null;
  count: null;
};

const initialState: initialStateProps = {
  loadId: [],
  isLoading: false,
  type: 'dispatch',
  data: [],
  error: null,
  count: null
};

interface dispatchDropdown {
  isLoading: boolean;
  dropdownList: null | any;
  error: string | null;
}

const dispatchDropdownState: dispatchDropdown = {
  isLoading: false,
  dropdownList: null,
  error: null
};

interface dispatchDropdownTrailer {
  isLoading: boolean;
  dropdownListTrailer: null | any;
  error: string | null;
}

const dispatchDropdownTrailerType: dispatchDropdownTrailer = {
  isLoading: false,
  dropdownListTrailer: null,
  error: null
};

const pagination = {
  pageNo: 1,
  pageSize: 5
};

// add dispatch
export const addDispatch = createAsyncThunk<ReturnedType, any>(
  'api/addDispatch',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.ADD_DISPATCH}`,
        data
      });
      await thunkApi.dispatch(getDispatchData(pagination));

      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// add dispatch slice
const addDispatchSlice = createSlice({
  name: 'addDispatch',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(addDispatch.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(addDispatch.fulfilled, (state, action: any) => {
      state.data = action?.payload?.data;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(addDispatch.rejected, (state, action: any) => {
      state.error = action?.error?.message || 'Something went wrong';
      state.isLoading = false;
    });
  }
});

//action for get dispatch data URLs.GET_DISPATCH_DATA

export const getDispatchData = createAsyncThunk<ReturnedType, any>(
  'api/getDispatchData',
  async (data, thunkApi) => {
    try {
      const {
        pageNo = 1,
        pageSize = 5,
        sortColumnName,
        sortBy,
        searchInput,
        searchColumnName,
        signal
      } = data;

      let url = `${URLs.GET_DISPATCH_DATA}?pageNo=${pageNo}&pageSize=${pageSize}`;

      if (sortBy && sortColumnName && searchColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}`;
      } else if (searchInput && searchColumnName) {
        url += `&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput && searchColumnName) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}`;
      } else if (searchInput) {
        url += `&searchInput=${searchInput}`;
      } else if (sortBy && sortColumnName) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: url,
        signal: signal
      });
      return response?.data;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: error?.response?.data?.message
              ? error?.response?.data?.message
              : error?.message,
            severity: 'error'
          })
        );
      }

      return thunkApi.rejectWithValue(error);
    }
  }
);

// reducer for get dispatch data

const dispatchDataSlice = createSlice({
  name: 'getDispatchData',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getDispatchData.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(getDispatchData.fulfilled, (state, action: any) => {
      const {
        data: { dispatchHistoryList, dispatchHistoryListCount }
      } = action.payload;
      state.data = dispatchHistoryList?.map((item: any, index: number) => ({
        ...item,
        id: item?.id,
        startDate: convertEpochToDateandTime(item?.startDate),
        endDate: convertEpochToDateandTime(item?.endDate),
        trailerId: item?.trailerId,
        loadId: item?.loadId,
        loadNumber: item?.loadId?.length,
        vehicleId: item?.vehicleId.toUpperCase(),
        freightId: item?.freightId || [],
        isActive: item?.isActive
      }));
      state.isLoading = false;
      state.count = dispatchHistoryListCount;
      state.error = null;
    });

    builder.addCase(getDispatchData.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something Went Wrong';
      state.isLoading = false;
    });
  }
});

//action for get dispatch list dropdown truckloadtype and loadtype
export const getDispatchList = createAsyncThunk<ReturnedType, any>(
  'dispatchlist',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.GET_DISPATCH_LIST}?truckLoadType=${data.truckLoadType}&loadType=${data.loadType}`
      });
      return response;
    } catch (error) {
      thunkApi.rejectWithValue(error);
    }
  }
);

//reducer for getting dispatch list dropdown
export const getDispatchSlice = createSlice({
  name: 'dispatchlist',
  initialState: dispatchDropdownState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getDispatchList.pending, state => {
      state.isLoading = true;
      state.dropdownList = null;
      state.error = null;
    });
    builder.addCase(getDispatchList.fulfilled, (state, action: any) => {
      state.isLoading = false;
      if (action.payload) {
        const {
          data: { data }
        } = action?.payload;
        state.dropdownList = data;
        state.error = null;
      }
    });
    builder.addCase(getDispatchList.rejected, (state, action: any) => {
      state.isLoading = false;
      state.dropdownList = null;
      state.error = action.payload?.response?.data?.error;
    });
  }
});

//action for get dispatch list dropdown trailertype
export const getDispatchTrailer = createAsyncThunk<ReturnedType, any>(
  'dispatchTrailer',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.GET_DISPATCH_LIST}?trailerType=${data}`
      });
      return response;
    } catch (error) {
      thunkApi.rejectWithValue(error);
    }
  }
);

//reducer for getting dispatch list trailer
export const getDispatchTrailerType = createSlice({
  name: 'dispatchlist',
  initialState: dispatchDropdownTrailerType,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getDispatchTrailer.pending, state => {
      state.isLoading = true;
      state.dropdownListTrailer = null;
      state.error = null;
    });
    builder.addCase(getDispatchTrailer.fulfilled, (state, action: any) => {
      state.isLoading = false;
      if (action?.payload) {
        const {
          data: { data }
        } = action?.payload;
        state.dropdownListTrailer = data;
        state.error = null;
      }
    });
    builder.addCase(getDispatchTrailer.rejected, (state, action: any) => {
      state.isLoading = false;
      state.dropdownListTrailer = null;
      state.error = action.payload?.response?.data?.error;
    });
  }
});

//deactivate dispatch data action

export const deactivateDispatchData = createAsyncThunk<ReturnedType, string>(
  'api/deactivateDispatchData',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PATCH',
        url: `${URLs.DEACTIVATE_DISPATCH_DATA}${data}`
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          Message: error.response?.data?.error
            ? error.response?.data?.error
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

//reducer for deactivate dispatch data

const deactivateDispatchDataSlice = createSlice({
  name: 'deactivateDispatchData',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(deactivateDispatchData.pending, state => {
      state.isLoading = true;
      state.error = null;
      state.type = 'deactivate';
    });
    builder.addCase(
      deactivateDispatchData.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.error = null;
        state.data = action.payload;
      }
    );
    builder.addCase(deactivateDispatchData.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action.payload?.response?.data?.error || 'Something went wrong';
    });
  }
});

// delete dispatch

export const deleteDispatch = createAsyncThunk<ReturnedType, ThunkArg>(
  'api/delete',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'DELETE',
        url: `${URLs.DELETE_DISPATCH}${data}`
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error
            ? error?.response?.data?.error
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

const deleteDispatchSlice = createSlice({
  name: 'delete',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(deleteDispatch.pending, state => {
      state.isLoading = true;
      state.error = null;
      state.type = 'delete';
    });
    builder.addCase(
      deleteDispatch.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.error = null;
        state.data = action.payload;
      }
    );
    builder.addCase(deleteDispatch.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action.payload?.response?.data?.error || 'Something went wrong';
    });
  }
});

// update dispatch
export const updateDispatch = createAsyncThunk<ReturnedType, any>(
  'api/update',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${URLs.UPDATE_DISPATCH}${data?.id}`,
        data: data.updatePayload
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      await thunkApi.dispatch(getDispatchData(pagination));
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error
            ? error?.response?.data?.error
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

const updateDispatchSlice = createSlice({
  name: 'updateDispatchSlice',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateDispatch.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      updateDispatch.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.error = null;
        state.data = action.payload;
      }
    );
    builder.addCase(updateDispatch.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action.payload?.response?.data?.error || 'Something went wrong';
    });
  }
});

export const updatedispatchReducer = updateDispatchSlice.reducer;
export const addDispatchReducer = addDispatchSlice.reducer;
export const dispatchDropdownData = getDispatchSlice.reducer;
export const getdispatchData = dispatchDataSlice.reducer;
export const deactivateDispatchReducer = deactivateDispatchDataSlice.reducer;
export const dispatchDropDownDataTrailer = getDispatchTrailerType.reducer;
export const deleteAlldispatch = deleteDispatchSlice.reducer;
